import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, UserRole } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Ban, LogOut, DollarSign, Loader2, AlertCircle } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, role, empresaStatus, loading, signOut } = useAuth();
  const [showSlowConnection, setShowSlowConnection] = useState(false);

  useEffect(() => {
    let timeout: any;
    if (loading && !showSlowConnection) {
      timeout = setTimeout(() => setShowSlowConnection(true), 8000);
    }
    return () => clearTimeout(timeout);
  }, [loading, showSlowConnection]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 text-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm font-medium text-muted-foreground">Verificando acesso...</p>
        </div>
        
        {showSlowConnection && (
          <div className="mt-8 animate-fade-in max-w-xs">
            <p className="text-sm text-muted-foreground mb-4">A verificação está demorando mais que o esperado...</p>
            <button 
              onClick={() => window.location.reload()}
              className="text-xs font-bold text-primary underline"
            >
              Recarregar página
            </button>
          </div>
        )}
      </div>
    );
  }

  // Authentication check
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Authorization check (Roles)
  if (!role) {
    // Se o usuário está logado mas o papel ainda não foi carregado/encontrado
    return <Navigate to="/redirect" replace />;
  }

  if (requiredRole && role !== requiredRole) {
    // If user is super_admin but route requires something else, or vice-versa
    if (role === "super_admin") return <Navigate to="/admin" replace />;
    if (role === "empresa_admin" || role === "gestor_rh" || role === "somente_leitura" || role === "funcionario") {
      return <Navigate to="/sistema/dashboard" replace />;
    }
    
    // Fallback for unauthorized
    return <Navigate to="/redirect" replace />;
  }

  // Account status check (only for company-related roles)
  if (role !== "super_admin" && empresaStatus && empresaStatus !== "ativa") {
    const statusMessages = {
      vencida: "Sua assinatura está vencida. Regularize seu faturamento para continuar utilizando.",
      bloqueada: "Seu acesso foi bloqueado temporariamente por questões administrativas.",
      cancelada: "A conta da sua empresa foi encerrada.",
    };

    const message = statusMessages[empresaStatus as keyof typeof statusMessages] || "O acesso da sua empresa está suspenso.";

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4 animate-in fade-in duration-500">
        <div className="text-center max-w-md w-full bg-card p-8 rounded-[2rem] shadow-2xl border border-border/50">
          <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Ban className="h-10 w-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-black mb-3 text-card-foreground uppercase tracking-tight">Acesso Suspenso</h1>
          <p className="text-muted-foreground mb-8 leading-relaxed">
            {message}
          </p>
          <div className="flex flex-col gap-3 w-full">
            {empresaStatus === "vencida" && (
              <Button asChild className="w-full gap-2 py-6 text-base font-bold rounded-xl shadow-lg shadow-primary/20">
                <a href="https://pay.cakto.com.br/37uv92w_894290" target="_blank" rel="noopener noreferrer">
                  <DollarSign className="h-5 w-5" />
                  Regularizar Assinatura
                </a>
              </Button>
            )}
            <div className="flex gap-4">
              <Button
                onClick={() => window.open("https://wa.me/5547997303469", "_blank")}
                variant="outline"
                className="flex-1 rounded-xl h-12 font-bold"
              >
                Suporte
              </Button>
              <Button 
                onClick={() => signOut()} 
                variant="ghost" 
                className="flex-1 gap-2 rounded-xl h-12 font-bold hover:bg-destructive/5 hover:text-destructive"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}